import React, { useEffect, useState } from 'react';
import '../styles/FilterColumnsPicker.css';

// The columns that name an item: a block may hide some, never all (matches MatrixPage / export.py).
const IDENTITY_COLUMNS = ['Key', 'Name', 'Synopsis'];
const TRACED_ITEMS = 'Traced Items Info';

// One read per filter + version for the life of the page; the wizard re-renders often.
const columnsCache = new Map();

function loadColumns(filterId, itemType, versionId) {
  const key = `${filterId}|${itemType}|${versionId}`;
  if (!columnsCache.has(key)) {
    const qs = new URLSearchParams({ item_type: itemType, version_id: versionId });
    const p = fetch(`/api/config/filter-columns/${filterId}?${qs}`)
      .then(r => r.json())
      .then(d => (d && d.IsSuccess ? d : Promise.reject(new Error((d && d.error) || 'Could not read the filter'))));
    p.catch(() => columnsCache.delete(key));   // a failure is retried next time, not remembered
    columnsCache.set(key, p);
  }
  return columnsCache.get(key);
}

/**
 * "Columns shown" for one block: every column the filter selects, ticked by default.
 * Unticking hides it in the grid and the exports only — the data, the trace matching and the
 * gaps are untouched (so hiding Traced Items Info is safe). Key / Name: at least one stays.
 * `hidden` / `onChange` carry the list of HIDDEN titles; [] = show all.
 */
function FilterColumnsPicker({ filterId, itemType, versionId, hidden, onChange }) {
  const [state, setState] = useState({ loading: false, columns: [], empty: false, error: '' });
  const hiddenList = hidden || [];

  useEffect(() => {
    if (!filterId || !itemType || !versionId) {
      setState({ loading: false, columns: [], empty: false, error: '' });
      return undefined;
    }
    let live = true;
    setState(s => ({ ...s, loading: true, error: '' }));
    loadColumns(filterId, itemType, versionId)
      .then(d => live && setState({ loading: false, columns: d.columns || [], empty: !!d.empty, error: '' }))
      .catch(e => live && setState({ loading: false, columns: [], empty: false, error: e.message }));
    return () => { live = false; };
  }, [filterId, itemType, versionId]);

  if (!filterId) return null;

  const { loading, columns, empty, error } = state;
  const identityPresent = columns.filter(c => IDENTITY_COLUMNS.includes(c));
  const identityShown = identityPresent.filter(c => !hiddenList.includes(c));
  const shownCount = columns.filter(c => !hiddenList.includes(c)).length;

  const toggle = (col) => {
    const next = hiddenList.includes(col)
      ? hiddenList.filter(c => c !== col)
      : [...hiddenList, col];
    onChange(next);
  };

  return (
    <div className="fcp">
      <div className="fcp-head">
        <span className="fcp-title">Columns shown</span>
        {columns.length > 0 && (
          <span className="fcp-count">{shownCount} of {columns.length}</span>
        )}
        {hiddenList.length > 0 && (
          <button type="button" className="fcp-reset" onClick={() => onChange([])}>Show all</button>
        )}
      </div>

      {loading && <div className="fcp-note">Reading the filter's columns…</div>}
      {!loading && error && <div className="fcp-note fcp-note--warn">{error} — all columns will be shown.</div>}
      {!loading && !error && empty && (
        <div className="fcp-note">This filter returned no items, so its columns can't be read — all columns will be shown.</div>
      )}

      {!loading && columns.length > 0 && (
        <div className="fcp-list">
          {columns.map(col => {
            const isShown = !hiddenList.includes(col);
            // The last shown Key / Name can't be unticked: a row needs something that names it.
            const locked = isShown && IDENTITY_COLUMNS.includes(col) && identityShown.length <= 1;
            return (
              <label
                key={col}
                className={`fcp-chip${isShown ? ' is-on' : ''}${locked ? ' is-locked' : ''}`}
                title={locked ? 'Key or Name must stay visible' : undefined}
              >
                <input type="checkbox" checked={isShown} disabled={locked} onChange={() => toggle(col)} />
                <span>{col}</span>
              </label>
            );
          })}
        </div>
      )}

      {hiddenList.includes(TRACED_ITEMS) && columns.includes(TRACED_ITEMS) && (
        <small className="fcp-hint">Traced Items Info is only hidden from view — the trace links are still read from it.</small>
      )}
    </div>
  );
}

export default FilterColumnsPicker;
